import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from './stores/auth'
import LoginView from './views/LoginView.vue'
import HomeView from './views/HomeView.vue'

// SdsBrandingView : page de branding réservée Neexus (super_admin_sds).
// Le nom de route '/sds/...' indique clairement que c'est une zone Neexus,
// pas une zone admin tenant.
const SdsBrandingView    = () => import('./views/AdminBrandingView.vue')
const ChantiersListView  = () => import('./views/ChantiersListView.vue')
const ChantierNewView    = () => import('./views/ChantierNewView.vue')
const ChantierDetailView = () => import('./views/ChantierDetailView.vue')
const ChantierEditView   = () => import('./views/ChantierEditView.vue')
const ChantierQrView     = () => import('./views/ChantierQrView.vue')
const PointageView       = () => import('./views/PointageView.vue')
const PointageScanView   = () => import('./views/PointageScanView.vue')
// PointagesListView et FichesHeuresListView ne sont plus referencees comme
// routes directes (redirigees vers /temps), elles sont chargees via TempsView
// qui les importe en tant que sous-composants.
const FicheHeuresDetailView = () => import('./views/FicheHeuresDetailView.vue')
const MesPointagesView      = () => import('./views/MesPointagesView.vue')
// DemandesListView / VehiculesListView / PaieSettingsView ne sont plus
// referencees directement comme routes — les anciennes URLs redirigent
// vers /gestion qui les importe en tant que sous-composants embedded.
const TempsView             = () => import('./views/TempsView.vue')
const VehiculeDetailView    = () => import('./views/VehiculeDetailView.vue')
const QuiConduisaitView     = () => import('./views/QuiConduisaitView.vue')
const GestionView           = () => import('./views/GestionView.vue')
const NotificationPreferencesView = () => import('./views/NotificationPreferencesView.vue')
const DemoLandingView       = () => import('./views/DemoLandingView.vue')

const router = createRouter({
  history: createWebHistory(),
  // Scroll en haut a chaque navigation vers une nouvelle route.
  // Si l'utilisateur utilise la fleche retour navigateur (savedPosition
  // present), on restaure sa position precedente. Sans ca chaque changement
  // d'onglet BottomNav atterrit au scroll de la page precedente.
  scrollBehavior(_to, _from, savedPosition) {
    if (savedPosition) return savedPosition;
    return { top: 0, left: 0 };
  },
  routes: [
    { path: '/login', name: 'login', component: LoginView, meta: { public: true } },
    // Landing publique demo (acces sans login pour pitch prospects)
    { path: '/demo',  name: 'demo',  component: DemoLandingView, meta: { public: true } },
    { path: '/', name: 'home', component: HomeView },
    {
      path: '/sds/branding',
      name: 'sds-branding',
      component: SdsBrandingView,
      meta: { superAdminSdsOnly: true },
    },
    { path: '/chantiers',           name: 'chantiers-list',   component: ChantiersListView },
    { path: '/chantiers/nouveau',   name: 'chantier-new',     component: ChantierNewView },
    { path: '/chantiers/:id',       name: 'chantier-detail',  component: ChantierDetailView },
    { path: '/chantiers/:id/modifier', name: 'chantier-edit', component: ChantierEditView },
    { path: '/chantiers/:id/qr',    name: 'chantier-qr',      component: ChantierQrView },
    { path: '/pointage',            name: 'pointage',         component: PointageView },
    { path: '/pointage/scan',       name: 'pointage-scan',    component: PointageScanView },
    // Page unifiee Temps (Pointages + Fiches d'heures en tabs internes).
    // Decision 2026-05-31 : les 2 modules etaient conceptuellement lies
    // (pointage genere fiche d'heures), separation en 2 onglets nav = friction.
    { path: '/temps',               name: 'temps',            component: TempsView },
    // Retrocompat : les anciennes URLs redirigent vers /temps avec le bon tab.
    // Garde les liens externes / bookmarks fonctionnels.
    { path: '/pointages',           name: 'pointages-list',   redirect: { name: 'temps', query: { tab: 'pointages' } } },
    { path: '/fiches-heures',       name: 'fiches-heures-list',   redirect: (to) => ({
        name: 'temps',
        query: { tab: 'fiches', ...to.query },
    }) },
    { path: '/fiches-heures/:id',   name: 'fiches-heures-detail', component: FicheHeuresDetailView },
    { path: '/mes-pointages',       name: 'mes-pointages',        component: MesPointagesView },
    // Page unifiee "Gestion" (Demandes / Vehicules / Paie en sous-tabs).
    // Refonte 2026-05-31 : regroupement des modules administratifs sur une
    // seule page (suit le pattern TempsView) pour aerer la BottomNav.
    { path: '/gestion',             name: 'gestion',              component: GestionView },
    // Retrocompat : anciennes URLs redirigent vers /gestion avec le bon tab.
    { path: '/demandes',            name: 'demandes-list',        redirect: { name: 'gestion', query: { tab: 'demandes' } } },
    { path: '/vehicules',           name: 'vehicules-list',       redirect: { name: 'gestion', query: { tab: 'vehicules' } } },
    { path: '/admin/paie-settings', name: 'paie-settings',        redirect: { name: 'gestion', query: { tab: 'paie' } },
      meta: { managementOnly: true } },
    // Routes vehicules dediees (vue detail + recherche) restent autonomes
    { path: '/vehicules/qui-conduisait',     name: 'qui-conduisait',  component: QuiConduisaitView },
    { path: '/vehicules/:id',                name: 'vehicule-detail', component: VehiculeDetailView },
    { path: '/preferences/notifications', name: 'notification-preferences', component: NotificationPreferencesView },
    { path: '/:catchAll(.*)', redirect: '/' },
  ],
})

router.beforeEach(async (to) => {
  const auth = useAuthStore()
  if (!to.meta.public && !auth.token) {
    return { name: 'login' }
  }
  if (to.name === 'login' && auth.token) {
    return { name: 'home' }
  }
  // Garantie : si l'user a un token mais que les details (role, tenant) ne
  // sont pas encore charges (cas du hard reload de page), on les fetch ICI
  // avant de proceder. Sinon les computed canShowQr / canEdit / etc. partent
  // sur un user undefined et cachent les boutons/sections accessibles, ce
  // qui donne l'impression d'une UI cassee apres reload.
  if (auth.token && !auth.user && !to.meta.public) {
    try {
      await auth.fetchMe()
    } catch {
      auth.logout()
      return { name: 'login' }
    }
  }
  if (to.meta.superAdminSdsOnly) {
    // Branding & configuration plateforme : reservé à Neexus uniquement
    // (modèle commercial — voir docs/PHASE_4_WHITE_LABEL.md)
    if (auth.user?.role !== 'super_admin_sds') {
      return { name: 'home' }
    }
  }
  if (to.meta.managementOnly) {
    // Pages admin (paie-settings, etc.) : admin / direction / super_admin_sds.
    const r = auth.user?.role
    if (r !== 'admin' && r !== 'direction' && r !== 'super_admin_sds') {
      return { name: 'home' }
    }
  }
})

export default router
